import {
  addDays,
  type FreedTime,
  type ItemPlan,
  parseLocalDate,
  startOfLocalDay,
  type TodayBrief,
  type TodayMeeting,
} from '@hugo/core'
import { formatDay, formatHours } from './format'

/**
 * Words for Today. Every number comes from the API (`today.brief`, hugo's planner); these
 * functions only put them into words.
 */

const TIME = (timeZone: string) =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })

/**
 * The line under "Today": the date and how much of it goes to tasks and promises.
 * "Wed 21 Oct · 4 h for tasks and promises, after 1.5 h of meetings".
 */
export function todaySummary(
  brief: Pick<TodayBrief, 'date' | 'workday' | 'timeOff' | 'time'>,
  thisYear: number,
): string {
  const day = formatDay(brief.date, thisYear)
  if (brief.timeOff) {
    const label = brief.timeOff.label ? ` (${brief.timeOff.label})` : ''
    return `${day} · You're away${label} until ${formatDay(brief.timeOff.endsOn, thisYear)}`
  }
  if (!brief.workday) return `${day} · No working hours today`
  const { workingMinutes, meetingMinutes, outOfOfficeMinutes, capacityMinutes } = brief.time
  if (workingMinutes === 0) return `${day} · The working day is over`
  if (capacityMinutes === 0) {
    return outOfOfficeMinutes > meetingMinutes
      ? `${day} · Out of office in your calendar`
      : `${day} · Meetings take the rest of the day`
  }
  const forTasks = `${todayDuration(capacityMinutes)} for tasks and promises`
  const taken = [
    meetingMinutes > 0 ? `${todayDuration(meetingMinutes)} of meetings` : null,
    outOfOfficeMinutes > 0 ? `${todayDuration(outOfOfficeMinutes)} out of office` : null,
  ].filter(Boolean)
  return taken.length > 0
    ? `${day} · ${forTasks}, after ${taken.join(' and ')}`
    : `${day} · ${forTasks}`
}

/**
 * A duration on Today: "30 min" under an hour, else hours like the hours-a-day setting ("1.5 h",
 * "4 h"). One format for the summary, the strip and the rows, so a number reads the same
 * everywhere on the page (and fits three cells and a row's meta line on a phone).
 */
export function todayDuration(minutes: number): string {
  return minutes < 60 ? `${minutes} min` : formatHours(minutes)
}

/** "3.5 h today", "30 min today": what hugo's plan does of an item today. */
export function plannedNote(minutes: number): string {
  return `${todayDuration(minutes)} today`
}

/**
 * When a meeting is, in the user's time zone: "09:30–10:00"; "All day" when it covers the whole
 * day (out of office); "until 12:00" / "from 16:00" when it starts or ends on another day.
 */
export function meetingTime(
  meeting: Pick<TodayMeeting, 'startsAt' | 'endsAt'>,
  date: string,
  timeZone: string,
): string {
  const day = parseLocalDate(date)
  const dayStart = startOfLocalDay(day, timeZone)
  const dayEnd = startOfLocalDay(addDays(day, 1), timeZone)
  const start = Date.parse(meeting.startsAt)
  const end = Date.parse(meeting.endsAt)
  const time = TIME(timeZone)
  if (start <= dayStart && end >= dayEnd) return 'All day'
  if (start < dayStart) return `until ${time.format(new Date(end))}`
  if (end > dayEnd) return `from ${time.format(new Date(start))}`
  return `${time.format(new Date(start))}–${time.format(new Date(end))}`
}

/** Whether a meeting is over at `now`. */
export function meetingOver(meeting: Pick<TodayMeeting, 'endsAt'>, now: Date): boolean {
  return Date.parse(meeting.endsAt) <= now.getTime()
}

/** "11:00–12:00 is free · 1 h": freed meeting time today and its free working minutes. */
export function freedHeading(
  freed: Pick<FreedTime, 'startsAt' | 'endsAt' | 'minutes'>,
  date: string,
  timeZone: string,
): string {
  return `${meetingTime(freed, date, timeZone)} is free · ${todayDuration(freed.minutes)}`
}

/** Why it is free: the meeting's title from its calendar, quoted. */
export function freedReason(title: string | null): string {
  return title ? `“${title}” no longer takes your time.` : 'A meeting no longer takes this time.'
}

/** "1 h of 7 h · late": how much of an item fits in the freed time, and why it comes first. */
export function freedNote(minutes: number, plan: Pick<ItemPlan, 'minutes' | 'status'>): string {
  const part =
    minutes < plan.minutes
      ? `${todayDuration(minutes)} of ${todayDuration(plan.minutes)}`
      : todayDuration(minutes)
  if (plan.status === 'overdue') return `${part} · overdue`
  if (plan.status === 'at_risk') return `${part} · late`
  return part
}
